import React, { useState, useEffect } from 'react';
import { formatTrDateTime } from '../utils/hopsDate';
import { 
  collection, 
  onSnapshot, 
  doc, 
  deleteDoc, 
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  runTransaction
} from 'firebase/firestore';
import { 
  ref, 
  getDownloadURL, 
  deleteObject, 
  uploadBytes,
  getStorage 
} from 'firebase/storage';
import { database, auth, storage } from './firebaseConfig';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Button,
  Box,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import PDFPreviewModal from './PDFPreviewModal';
import './CopKutusu.css';

const CopKutusu = () => {
  const [deletedPuantajlar, setDeletedPuantajlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedPuantaj, setSelectedPuantaj] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [puantajToDelete, setPuantajToDelete] = useState(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [puantajToRestore, setPuantajToRestore] = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setError('Kullanıcı oturumu bulunamadı');
      setLoading(false);
      return;
    }

    // Silinen puantajları dinle
    const unsubscribe = onSnapshot(
      collection(database, `users/${user.email}/silinen_puantajlar`),
      (snapshot) => {
        const puantajlar = [];
        snapshot.forEach((doc) => {
          puantajlar.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Silinme tarihine göre sırala (en yeni önce)
        puantajlar.sort((a, b) => {
          // Firebase Timestamp objelerini handle et
          const getTime = (dateValue) => {
            if (!dateValue) return 0;
            if (dateValue.seconds) {
              return dateValue.seconds * 1000; // Firebase Timestamp
            }
            return new Date(dateValue).getTime(); // Normal date string
          };
          
          const dateA = getTime(a.deletedAt);
          const dateB = getTime(b.deletedAt);
          return dateB - dateA;
        });
        
        setDeletedPuantajlar(puantajlar);
        setLoading(false);
      },
      (error) => {
        console.error('Silinen puantajları yüklerken hata:', error);
        setError('Veriler yüklenirken hata oluştu');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const formatDate = (dateValue, isSimpleDate = false) => {
    if (!dateValue) return '-';
    
    // Firebase Timestamp objesi kontrolü
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      return formatTrDateTime(new Date(dateValue.seconds * 1000));
    }
    
    // String'e güvenli dönüştürme
    let dateString;
    try {
      dateString = String(dateValue);
    } catch (e) {
      return '-';
    }
    
    // String kontrolü
    if (typeof dateString !== 'string') {
      return '-';
    }
    
    // DD.MM.YYYY formatındaki tarihleri kontrol et
    if (isSimpleDate && dateString.match && dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return dateString; // Zaten doğru formatta
    }
    
    try {
      // ISO date string ise
      if (dateString.includes && (dateString.includes('T') || dateString.includes('-'))) {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        return formatTrDateTime(date);
      }
      
      // DD.MM.YYYY formatındaki tarihi parse et
      if (dateString.match && dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        return dateString; // Sadece tarihi göster
      }
      
      return dateString;
    } catch (error) {
      return dateString || '-';
    }
  };

  const handlePreview = async (puantaj) => {
    if (!puantaj.pdfUrl) {
      setError('PDF bulunamadı');
      setAlertOpen(true);
      return;
    }
    
    setPreviewUrl(puantaj.pdfUrl);
    setSelectedPuantaj(puantaj);
    setIsPreviewOpen(true);
  };

  const handleDownload = async (puantaj) => {
    if (!puantaj.pdfUrl) {
      setError('PDF bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // Firebase Storage URL'leri için direkt indirme linki oluştur
      if (puantaj.pdfUrl.includes('firebasestorage.googleapis.com')) {
        // Direkt indirme için link oluştur
        const link = document.createElement('a');
        link.href = puantaj.pdfUrl;
        link.download = puantaj.pdfName || `puantaj_${puantaj.id}.pdf`;
        link.target = '_blank'; // Yeni sekmede aç
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ PDF indirme başlatıldı');
        return;
      }
      
      // Diğer URL'ler için fetch kullan
      const response = await fetch(puantaj.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = puantaj.pdfName || `puantaj_${puantaj.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('İndirme hatası:', error);
      // CORS hatası durumunda direkt link ile dene
      if (error.message.includes('Failed to fetch')) {
        const link = document.createElement('a');
        link.href = puantaj.pdfUrl;
        link.download = puantaj.pdfName || `puantaj_${puantaj.id}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ PDF alternatif yöntemle indirildi');
      } else {
        setError('PDF indirilirken hata oluştu');
        setAlertOpen(true);
      }
    }
  };

  const handleRestoreClick = (puantaj) => {
    setPuantajToRestore(puantaj);
    setRestoreConfirmOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!puantajToRestore) return;
    
    const user = auth.currentUser;
    if (!user) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // PDF URL'i zaten orijinal konumu gösteriyor, değiştirmeye gerek yok
      // CORS nedeniyle client-side'da PDF taşıma yapamıyoruz
      console.log('📄 PDF orijinal konumunda kalacak:', puantajToRestore.pdfUrl);
      
      // Puantajı orijinal koleksiyona geri ekle
      const restoredData = { ...puantajToRestore };
      delete restoredData.deletedAt;
      delete restoredData.deletedBy;
      delete restoredData.originalId;
      delete restoredData.pdfRef; // pdfRef Reference objesi olduğu için kaldır
      
      const originalId = puantajToRestore.originalId || puantajToRestore.id;
      const originalRef = doc(database, `users/${user.email}/timeSheets`, originalId);
      await setDoc(originalRef, {
        ...restoredData,
        restoredAt: new Date().toISOString(),
        restoredBy: user.email
      });
      
      // Cari Kasa'ya borcu geri ekle
      const customerName = puantajToRestore['Müşteri Adı'] || puantajToRestore.customerName;
      const machineName = puantajToRestore['Makine İsmi'] || puantajToRestore.machineName;
      
      if (customerName && puantajToRestore.deletedCariInfo) {
        console.log('💰 Cari kasa orijinal değerlerle güncelleniyor:', puantajToRestore.deletedCariInfo);
        
        // Müşteriyi bul
        const customerRef = collection(database, `users/${user.email}/customerList`);
        const customerQuery = query(customerRef, where("Müşteri Adı", "==", customerName));
        const customerSnapshot = await getDocs(customerQuery);
        
        if (!customerSnapshot.empty) {
          const customerDoc = customerSnapshot.docs[0];
          const customerId = customerDoc.id;
          const debtRef = doc(database, `users/${user.email}/customerList/${customerId}/debt/current`);
          
          // Orijinal cari bilgilerini kullan
          const cariInfo = puantajToRestore.deletedCariInfo;
          const restoredAmount = cariInfo.amount || 0;
          const originalDate = cariInfo.date || new Date().toISOString();
          
          // Transaction ile borcu güncelle
          await runTransaction(database, async (transaction) => {
            const debtDoc = await transaction.get(debtRef);
            
            if (!debtDoc.exists()) {
              // Borç belgesi yoksa oluştur
              transaction.set(debtRef, {
                totalDebt: restoredAmount,
                history: [{
                  date: originalDate, // Orijinal tarihi kullan
                  type: 'increase',
                  amount: restoredAmount,
                  description: cariInfo.description || `Geri yüklenen puantaj: ${machineName}`,
                  machineName: cariInfo.machineName || machineName,
                  machineHours: cariInfo.machineHours || 0,
                  hourlyRate: cariInfo.hourlyRate || 0,
                  puantajCode: cariInfo.puantajCode || originalId.slice(-8),
                  restoredPuantaj: true,
                  restoredAt: new Date().toISOString()
                }]
              });
            } else {
              // Mevcut borç belgesini güncelle
              const currentData = debtDoc.data();
              const newTotalDebt = (currentData.totalDebt || 0) + restoredAmount;
              const newHistory = [...(currentData.history || []), {
                date: originalDate, // Orijinal tarihi kullan
                type: 'increase',
                amount: restoredAmount,
                description: cariInfo.description || `Geri yüklenen puantaj: ${machineName}`,
                machineName: cariInfo.machineName || machineName,
                machineHours: cariInfo.machineHours || 0,
                hourlyRate: cariInfo.hourlyRate || 0,
                puantajCode: cariInfo.puantajCode || originalId.slice(-8),
                restoredPuantaj: true,
                restoredAt: new Date().toISOString()
              }];
              
              transaction.update(debtRef, {
                totalDebt: newTotalDebt,
                history: newHistory
              });
            }
          });
          
          console.log('✅ Cari kasa orijinal değerlerle güncellendi:', {
            tutar: restoredAmount,
            tarih: originalDate,
            saatlikÜcret: cariInfo.hourlyRate,
            çalışmaSaati: cariInfo.machineHours
          });
        } else {
          console.warn('⚠️ Müşteri bulunamadı, cari kasa güncellenemedi:', customerName);
        }
      } else if (customerName) {
        // Eğer deletedCariInfo yoksa (eski silinen puantajlar için), çalışma saatlerinden hesapla
        console.log('⚠️ Orijinal cari bilgisi bulunamadı, çalışma saatlerinden hesaplanıyor');
        
        // Müşteriyi bul
        const customerRef = collection(database, `users/${user.email}/customerList`);
        const customerQuery = query(customerRef, where("Müşteri Adı", "==", customerName));
        const customerSnapshot = await getDocs(customerQuery);
        
        if (!customerSnapshot.empty) {
          const customerDoc = customerSnapshot.docs[0];
          const customerId = customerDoc.id;
          const debtRef = doc(database, `users/${user.email}/customerList/${customerId}/debt/current`);
          
          // Çalışma saatlerini hesapla
          let totalHours = 0;
          if (puantajToRestore['1. Başlangıç Saati'] && puantajToRestore['1. Bitiş Saati']) {
            const start1 = puantajToRestore['1. Başlangıç Saati'].split(':');
            const end1 = puantajToRestore['1. Bitiş Saati'].split(':');
            const hours1 = parseInt(end1[0]) - parseInt(start1[0]);
            const minutes1 = parseInt(end1[1]) - parseInt(start1[1]);
            totalHours += hours1 + (minutes1 / 60);
          }
          
          if (puantajToRestore['2. Başlangıç Saati'] && puantajToRestore['2. Bitiş Saati']) {
            const start2 = puantajToRestore['2. Başlangıç Saati'].split(':');
            const end2 = puantajToRestore['2. Bitiş Saati'].split(':');
            const hours2 = parseInt(end2[0]) - parseInt(start2[0]);
            const minutes2 = parseInt(end2[1]) - parseInt(start2[1]);
            totalHours += hours2 + (minutes2 / 60);
          }
          
          // Makine bilgisini al ve saatlik ücreti hesapla
          const machinesRef = collection(database, `users/${user.email}/makineListesi`);
          const machineQuery = query(machinesRef, where("Makine İsmi", "==", machineName));
          const machineSnapshot = await getDocs(machineQuery);
          
          let hourlyRate = 0;
          if (!machineSnapshot.empty) {
            const machineData = machineSnapshot.docs[0].data();
            hourlyRate = parseFloat(machineData['Makine Saatlik Fiyatı'] || 0);
          }
          
          const totalAmount = totalHours * hourlyRate;
          
          // Transaction ile borcu güncelle
          await runTransaction(database, async (transaction) => {
            const debtDoc = await transaction.get(debtRef);
            
            if (!debtDoc.exists()) {
              // Borç belgesi yoksa oluştur
              transaction.set(debtRef, {
                totalDebt: totalAmount,
                history: [{
                  date: puantajToRestore['Tarih'] || new Date().toISOString(),
                  type: 'increase',
                  amount: totalAmount,
                  description: `Geri yüklenen puantaj: ${machineName}`,
                  machineName: machineName,
                  machineHours: totalHours,
                  hourlyRate: hourlyRate,
                  puantajCode: originalId.slice(-8),
                  restoredPuantaj: true
                }]
              });
            } else {
              // Mevcut borç belgesini güncelle
              const currentData = debtDoc.data();
              const newTotalDebt = (currentData.totalDebt || 0) + totalAmount;
              const newHistory = [...(currentData.history || []), {
                date: puantajToRestore['Tarih'] || new Date().toISOString(),
                type: 'increase',
                amount: totalAmount,
                description: `Geri yüklenen puantaj: ${machineName}`,
                machineName: machineName,
                machineHours: totalHours,
                hourlyRate: hourlyRate,
                puantajCode: originalId.slice(-8),
                restoredPuantaj: true
              }];
              
              transaction.update(debtRef, {
                totalDebt: newTotalDebt,
                history: newHistory
              });
            }
          });
          
          console.log('✅ Cari kasa hesaplanan değerlerle güncellendi:', {
            tutar: totalAmount,
            çalışmaSaati: totalHours,
            saatlikÜcret: hourlyRate
          });
        }
      }
      
      // Silinen_puantajlar'dan kaldır
      await deleteDoc(doc(database, `users/${user.email}/silinen_puantajlar`, puantajToRestore.id));
      
      setSuccess('Puantaj başarıyla geri yüklendi ve cari kasa güncellendi!');
      setAlertOpen(true);
      setRestoreConfirmOpen(false);
      setPuantajToRestore(null);
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      setError('Puantaj geri yüklenirken hata oluştu');
      setAlertOpen(true);
    }
  };

  const handleDeleteClick = (puantaj) => {
    setPuantajToDelete(puantaj);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteForever = async () => {
    if (!puantajToDelete) return;
    
    const user = auth.currentUser;
    if (!user) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // PDF'i kalıcı olarak sil (orijinal konumundan)
      if (puantajToDelete.pdfUrl) {
        try {
          // URL'den storage path'ini çıkar
          const storagePath = decodeURIComponent(puantajToDelete.pdfUrl.split('/o/')[1].split('?')[0]);
          const pdfRef = ref(storage, storagePath);
          await deleteObject(pdfRef);
          console.log('✅ PDF kalıcı olarak silindi:', storagePath);
        } catch (pdfError) {
          console.error('PDF silme hatası:', pdfError);
          // PDF silinemese bile Firestore kaydını sil
        }
      }
      
      // Firestore'dan kalıcı olarak sil
      await deleteDoc(doc(database, `users/${user.email}/silinen_puantajlar`, puantajToDelete.id));
      
      setSuccess('Puantaj kalıcı olarak silindi!');
      setAlertOpen(true);
      setDeleteConfirmOpen(false);
      setPuantajToDelete(null);
    } catch (error) {
      console.error('Kalıcı silme hatası:', error);
      setError('Puantaj silinirken hata oluştu');
      setAlertOpen(true);
    }
  };

  const getPuantajTitle = (puantaj) => {
    const customerName = puantaj['Müşteri Adı'] || puantaj.customerName || 'Bilinmeyen';
    const machineName = puantaj['Makine İsmi'] || puantaj.machineName || 'Bilinmeyen';
    // Tarihi başlıkta gösterme, ayrı kolonda var
    return `${customerName} - ${machineName}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="cop-kutusu-container">
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Çöp Kutusu
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Silinen puantajlarınız burada listelenir. Puantajları geri yükleyebilir veya kalıcı olarak silebilirsiniz.
        </Typography>
      </Box>

      {deletedPuantajlar.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Çöp kutusu boş
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Henüz silinmiş puantaj bulunmuyor.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Puantaj Adı</TableCell>
                <TableCell>Kaydedilme Tarihi</TableCell>
                <TableCell>Silinme Tarihi</TableCell>
                <TableCell>Silen Kullanıcı</TableCell>
                <TableCell align="center">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deletedPuantajlar.map((puantaj) => (
                <TableRow key={puantaj.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {getPuantajTitle(puantaj)}
                    </Typography>
                    {puantaj.pdfName && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {puantaj.pdfName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(puantaj.createdAt || puantaj['Tarih'], true)}
                  </TableCell>
                  <TableCell>
                    {formatDate(puantaj.deletedAt)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={puantaj.deletedBy || 'Bilinmeyen'} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Önizle">
                      <IconButton 
                        onClick={() => handlePreview(puantaj)}
                        color="info"
                        size="small"
                      >
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="İndir">
                      <IconButton 
                        onClick={() => handleDownload(puantaj)}
                        color="primary"
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Geri Yükle">
                      <IconButton 
                        onClick={() => handleRestoreClick(puantaj)}
                        color="success"
                        size="small"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Kalıcı Olarak Sil">
                      <IconButton 
                        onClick={() => handleDeleteClick(puantaj)}
                        color="error"
                        size="small"
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* PDF Önizleme Modal */}
      {isPreviewOpen && (
        <PDFPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewUrl(null);
            setSelectedPuantaj(null);
          }}
          onReject={() => {
            setIsPreviewOpen(false);
            setPreviewUrl(null);
            setSelectedPuantaj(null);
          }}
          pdfUrl={previewUrl}
          puantajData={selectedPuantaj}
          title={selectedPuantaj ? getPuantajTitle(selectedPuantaj) : 'PDF Önizleme'}
          showApproveButton={false}
        />
      )}

      {/* Geri Yükleme Onay Dialog */}
      <Dialog
        open={restoreConfirmOpen}
        onClose={() => setRestoreConfirmOpen(false)}
      >
        <DialogTitle>Puantajı Geri Yükle</DialogTitle>
        <DialogContent>
          <Typography>
            Bu puantajı geri yüklemek istediğinizden emin misiniz?
          </Typography>
          {puantajToRestore && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Puantaj:</strong> {getPuantajTitle(puantajToRestore)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleRestoreConfirm} color="success" variant="contained">
            Geri Yükle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kalıcı Silme Onay Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Kalıcı Olarak Sil</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bu işlem geri alınamaz! Puantaj kalıcı olarak silinecektir.
          </Alert>
          <Typography>
            Bu puantajı kalıcı olarak silmek istediğinizden emin misiniz?
          </Typography>
          {puantajToDelete && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Puantaj:</strong> {getPuantajTitle(puantajToDelete)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteForever} color="error" variant="contained">
            Kalıcı Olarak Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CopKutusu;
